// @ts-strict-ignore
import {Translatable} from 'common/i18n';
import {PropertyType} from 'common/report/constants';
import {DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {
  BooleanOperator,
  FilterOperator,
  ListOperator,
  NumberOperator,
  ObjectOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {DisplaySize, Placement, TooltipAttrs} from 'common/components/tooltip/types';

export type SelectedOperator = [FilterOperator, OperatorDescription];

export interface OperatorDescription {
  symbol: Translatable;
  label: string;
  tooltip?: TooltipAttrs;
}

export const OPERATORS_WITH_UNIT: FilterOperator[] = [
  NumberOperator.AtLeast,
  NumberOperator.AtMost,
  NumberOperator.EqualTo,
  NumberOperator.NotEqualTo,
  NumberOperator.GreaterThan,
  NumberOperator.LessThan,
  NumberOperator.Between,
  NumberOperator.NotBetween,
];

/**
 * Defines a symbol and description for each filter operator, on every property type
 * this list is sorted by usage metrics on p3 https://mixpanel.com/s/2ebosG
 */
export const PropertyTypeOperatorMap = new Map<PropertyType, {[key in FilterOperator]?: OperatorDescription}>([
  [
    PropertyType.Datetime,
    {
      [DateRangeType.RelativeAfter]: {
        symbol: ({t}) => t(`filters.date-operator.last`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.last`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.NotRelativeAfter]: {
        symbol: ({t}) => t(`filters.date-operator.not-in-last`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.not-in-last`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.Between]: {
        symbol: ({t}) => t(`filters.date-operator.between`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.between`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.NotBetween]: {
        symbol: ({t}) => t(`filters.date-operator.not-between`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.not-between`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.On]: {
        symbol: ({t}) => t(`filters.date-operator.on`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.on`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.NotOn]: {
        symbol: ({t}) => t(`filters.date-operator.not-on`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.not-on`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.RelativeBefore]: {
        symbol: ({t}) => t(`filters.date-operator.before-last`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.before-last`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.Before]: {
        symbol: ({t}) => t(`filters.date-operator.before`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.before`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.Since]: {
        symbol: ({t}) => t(`filters.date-operator.since`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.since`),
          size: DisplaySize.Small,
        },
      },
      [DateRangeType.RelativeFuture]: {
        symbol: ({t}) => t(`filters.date-operator.in-the-next`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.date-operator.tooltip.in-the-next`),
          size: DisplaySize.Small,
        },
      },
    },
  ],
  [
    PropertyType.Object,
    {
      [ObjectOperator.Equals]: {symbol: `=`, label: `equals`},
      [ObjectOperator.Set]: {symbol: `{=}`, label: `is set`},
    },
  ],
  [
    PropertyType.List,
    {
      [ListOperator.Contains]: {symbol: `∋`, label: `contains`},
      [ListOperator.DoesNotContain]: {symbol: `∌`, label: `does not contain`},
    },
  ],
  [
    PropertyType.Number,
    {
      [NumberOperator.EqualTo]: {
        symbol: ({t}) => t(`filters.number-operator.equals`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.equals`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.NotEqualTo]: {
        symbol: ({t}) => t(`filters.number-operator.not-equal`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.not-equal`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.GreaterThan]: {
        symbol: ({t}) => t(`filters.number-operator.greater-than`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.greater-than`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.AtLeast]: {
        symbol: ({t}) => t(`filters.number-operator.greater-than-equal`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.greater-than-equal`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.LessThan]: {
        symbol: ({t}) => t(`filters.number-operator.less-than`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.less-than`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.AtMost]: {
        symbol: ({t}) => t(`filters.number-operator.less-than-equal`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.less-than-equal`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.Between]: {
        symbol: ({t}) => t(`filters.number-operator.between`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.between`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.NotBetween]: {
        symbol: ({t}) => t(`filters.number-operator.not-between`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.not-between`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.Set]: {
        symbol: ({t}) => t(`filters.number-operator.is-numeric`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.is-numeric`),
          size: DisplaySize.Small,
        },
      },
      [NumberOperator.NotSet]: {
        symbol: ({t}) => t(`filters.number-operator.is-not-numeric`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.number-operator.tooltip.is-not-numeric`),
          size: DisplaySize.Small,
        },
      },
    },
  ],
  [
    PropertyType.Boolean,
    {
      [BooleanOperator.True]: {symbol: ({t}) => t(`filters.boolean-operator.is-true`), label: null},
      [BooleanOperator.False]: {symbol: ({t}) => t(`filters.boolean-operator.is-false`), label: null},
    },
  ],
  [
    PropertyType.String,
    {
      [StringOperator.Equals]: {
        symbol: ({t}) => t(`filters.string-operator.is`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.string-operator.tooltip.is`),
          size: DisplaySize.Small,
        },
      },
      [StringOperator.DoesNotEqual]: {
        symbol: ({t}) => t(`filters.string-operator.is-not`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.string-operator.tooltip.is-not`),
          size: DisplaySize.Small,
        },
      },
      [StringOperator.Contains]: {
        symbol: ({t}) => t(`filters.string-operator.contains`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.string-operator.tooltip.contains`),
          size: DisplaySize.Small,
        },
      },
      [StringOperator.DoesNotContain]: {
        symbol: ({t}) => t(`filters.string-operator.does-not-contain`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.string-operator.tooltip.does-not-contain`),
          size: DisplaySize.Small,
        },
      },
      [StringOperator.Set]: {
        symbol: ({t}) => t(`filters.string-operator.is-set`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.string-operator.tooltip.is-set`),
          size: DisplaySize.Small,
        },
      },
      [StringOperator.NotSet]: {
        symbol: ({t}) => t(`filters.string-operator.is-not-set`),
        label: ``,
        tooltip: {
          placement: Placement.Right,
          text: ({t}) => t(`filters.string-operator.tooltip.is-not-set`),
          size: DisplaySize.Small,
        },
      },
    },
  ],
]);
